import React from 'react';
import {
    Button, Box, Container, Typography, Divider, Dialog,
    DialogTitle, DialogContent, FormControl, InputLabel,
    Select, MenuItem, DialogActions,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Menu,
    LinearProgress,
    FormHelperText,
    Grid
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import ApplicationContext from '../contexts/application';
import { CustomDataGrid } from '../components/CustomDataGrid';
import { deleteAlarmTypes, getAlarmGroups, getAlarmPriorities, getAlarmTypes, getMessageSources, importAlarmFile, saveAlarmTypes } from '../libs/apiCalls';
import { config } from '../libs/config';
import ConfirmDialog from '../components/ConfirmDialog';
import CustomFilter from '../components/CustomFilter';
import { useCan } from '../auth/useCan';

// Column split shared by the Alarm Destinations and Options sections of the dialog, so their
// second columns line up with each other. Defined once because the whole point is that the two
// agree - they were 35% and 50% before, sized with flexGrow, which also made each row's columns
// depend on how wide the labels in the other column happened to be.
//
// The first column is a fixed width, not a percentage, because what has to fit in it is a fixed
// width: the helper line '* Cleared from Alarm Viewer regularly.' runs to about 210px at the
// 12px FormHelperText size, and the 35% it had of this dialog's ~552px of content was 193px, so
// it wrapped. A percentage would also grow the column for no reason if the dialog ever widened;
// this way the second column absorbs the extra. minmax(0, 1fr) rather than 1fr so a narrower
// viewport shrinks that column instead of overflowing the dialog.
//
// Second column keeps ~296px against the ~258px its own longest helper line needs. Both are
// snug, so a longer label in either section is what to suspect if one starts wrapping again.
const DIALOG_COLUMNS = '240px minmax(0, 1fr)';

function AlarmTypes() {
    // 'priority' routes on the alarm priority instead, so there are no destinations to show.
    // See config.alarmRouting for the three modes.
    const SHOW_DESTINATIONS = config.alarmRouting !== 'priority';
    // Which alarm groups an alarm type reaches. Set here in every mode except 'group', where
    // the screen behind an alarm group row does it per group instead.
    //
    // 'priority' needs this as much as 'type' does: that mode has no per-group screen, and its
    // email destination takes its recipients from exactly these groups - so hiding it would
    // leave a site able to switch email on for a priority and unable to say who receives it.
    const SHOW_GROUPS = config.alarmRouting !== 'group';

    const defaultPageSize = 30;

    const { settings, setDialog, setSnackbar } = React.useContext(ApplicationContext);
    const [gridRows, setGridRows] = React.useState([]);
    const [gridSelection, setGridSelection] = React.useState([]);
    const [selectedRow, setSelectedRow] = React.useState({});
    const [openDialog, setOpenDialog] = React.useState(false);
    const [openDelete, setOpenDelete] = React.useState(false);
    const [reload, setReload] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [isNotification, setIsNotification] = React.useState(false);
    const [isDelayed, setIsDelayed] = React.useState(false);
    const canDelete = useCan('URN:TWX:ESB;;Alarms:Types:Delete', 'write').allow;

    // A site capability, not something this screen changes - a const, not state.

    const [sourceList, setSourceList] = React.useState([]);
    const [priorityList, setPriorityList] = React.useState([]);
    const [selectedPriority, setSelectedPriority] = React.useState({});
    const [selectedSource, setSelectedSource] = React.useState({});
    const [alarmGroups, setAlarmGroups] = React.useState([]);
    const [selectedGroups, setSelectedGroups] = React.useState([]);
    const [anchor, setAnchor] = React.useState(null);
    const [gridColumns, setGridColumns] = React.useState([]);
    const importFile = React.useRef(null);
    const open = Boolean(anchor);

    const [serverData, setServerData] = React.useState({
        records: [],
        currentPage: 0,
        totalPages: 0,
        totalRecords: 0
    });

    const filter = React.useRef({
        searchString: '',
        fromDate: '',
        toDate: '',
        priority: '',
        topic: '',
        status: '',
        source: '',
        roles: settings.roles
    });

    const columns = [
        { field: 'id', headerName: 'ID' },
        { field: 'source', headerName: 'Source', flex: 0.1 },
        { field: 'alarmType', headerName: 'Alarm Type', flex: 0.5 },
        { field: 'priority', headerName: 'Priority', flex: 0.2 }
    ];

    const clearSelection = () => {
        setGridSelection([]);
        setSelectedRow({});
        setSelectedPriority({});
        setSelectedSource({});
        setSelectedGroups([]);

        importFile.current.value = null;
    };

    const handleNew = () => {
        const alarmType = {
            id: 0,
            sourceId: '',
            priorityId: '',
            alarmType: '',
            priority: '',
            notification: false,
            delayed: false,
            alarmGroupIds: []
        };

        setSelectedRow(alarmType);

        // The dialog's checkbox state lives outside selectedRow, so a new record has to clear it
        // explicitly or it opens holding whatever the last selected row had. That was cosmetic
        // for the groups while the assignment was never saved; now it would assign them.
        setSelectedGroups([]);
        setIsNotification(false);
        setIsDelayed(false);

        setAnchor(null);
        setOpenDialog(true);
        setGridSelection([]);
    };

    const handleEdit = () => {
        if (Object.keys(selectedRow).length > 0) {
            // Re-read from the grid rather than trusting what selection left behind: a save
            // refreshes the rows underneath, and this dialog should show what was saved.
            loadDialogFrom(gridRows.find((row) => row.id === selectedRow.id) ?? selectedRow);
            setOpenDialog(true);
        }
    };

    const handleDelete = () => {
        if (Object.keys(selectedRow).length > 0) {
            setOpenDelete(true);
        }
    };

    /**
     * Fills the dialog from a row as the grid currently holds it.
     *
     * Called both when the selection changes and when the dialog opens. Opening used to rely on
     * the selection event alone, which is not reliable after a save: clearSelection() empties
     * this state, but the grid's own selection is uncontrolled - no rowSelectionModel is passed -
     * so it still holds the row, and clicking that same row again is not a change and raises no
     * event. The dialog then showed the cleared state while the grid showed the saved data.
     */
    const loadDialogFrom = (row) => {
        if (!row) {
            return;
        }

        setSelectedRow(row);
        setSelectedPriority(priorityList.find((item) => item.id === row.priorityId));
        setSelectedSource(sourceList.find((item) => item.sourceId === row.sourceId));
        // The server builds this with STRING_AGG(..., ', '), so every id after the first arrives
        // with a leading space and would match no checkbox.
        setSelectedGroups(row.alarmGroupIds
            ? String(row.alarmGroupIds).split(',').map((id) => id.trim()).filter((id) => id !== '')
            : []);
        setIsNotification(row.notification);
        setIsDelayed(row.delayed);
    };

    const handleRowSelected = (ids) => {
        setGridSelection(ids);

        if (ids.length > 0) {
            var row = gridRows.find((row) => row.id === ids[0]);

            // Only on a real change of row: re-running this for the row already loaded would
            // discard whatever is being edited in an open dialog.
            if (row && selectedRow.id !== row.id) {
                loadDialogFrom(row);
            }
        }
    };

    const handlePageModelChanged = (event) => {
        setPage(event.page);
        setPageSize(event.pageSize);

        getData(filter.current, event.page, event.pageSize);
    };

    const handleCellDoubleClick = (params, event) => {
        // params.row is the row as the grid holds it now, so the dialog opens on current data
        // whether or not selecting it raised an event.
        loadDialogFrom(params.row);
        setOpenDialog(true);
    };

    const getData = (filter, page, pageSize) => {
        filter.roles = settings.roles;
        setLoading(true);

        getAlarmTypes(settings.token, JSON.stringify(filter), page, pageSize,
            (response) => {
                if (!response.data.error) {
                    setServerData({
                        records: JSON.parse(response.data.result),
                        currentPage: response.data.currentPage,
                        totalPages: response.data.totalPages,
                        totalRecords: response.data.totalRecords
                    });

                    setGridRows(JSON.parse(response.data.result));
                    setLoading(false);
                }
                else {
                    setDialog(response.data.errors[0], true);
                    setLoading(false);
                }
            },
            (error) => {
                setDialog(error.message, true);
            }
        )

        if (sourceList.length === 0) {
            setLoading(true);

            getMessageSources(settings.token, 0, 100, 'Name', 0,
                (response) => {
                    if (!response.data.error) {
                        setSourceList(JSON.parse(response.data.result));
                    }
                    else {
                        setDialog(response.data.errors[0], true);
                    }

                    setLoading(false);
                },
                (error) => {
                    setDialog(error.message, true);
                    setLoading(false);
                }
            );
        }

        if (priorityList.length === 0) {
            setLoading(true);

            getAlarmPriorities(settings.token,
                (response) => {
                    if (!response.data.error) {
                        setPriorityList(JSON.parse(response.data.result));
                    }
                    else {
                        setDialog(response.data.errors[0], true);
                        setLoading(false);
                    }
                },
                (error) => {
                    setDialog(error.message, true);
                    setLoading(false);
                }
            );
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setReload(!reload);

        clearSelection();
    };

    const handleSubmitDialog = (event) => {
        event.preventDefault();
        setOpenDialog(false);

        if (Object.keys(selectedRow).length > 0) {
            setLoading(true);

            const alarmType = {
                id: selectedRow.id,
                sourceId: selectedSource.sourceId,
                priorityId: selectedPriority.id,
                alarmType: selectedRow.alarmType,
                priority: selectedPriority,
                notification: selectedRow.notification,
                delayed: selectedRow.delayed,
                alarmGroupIds: selectedGroups,
                route01: selectedRow.route01,
                route02: selectedRow.route02,
                route03: selectedRow.route03,
                route04: selectedRow.route04
            };

            if (selectedRow.id > 0) {
                alarmType.createdBy = selectedRow.createdBy;
                alarmType.createdDate = selectedRow.createdDate;

                alarmType.updatedBy = settings.userName;
                alarmType.updatedDate = new Date();
            }
            else {
                alarmType.createdBy = settings.userName;
                alarmType.createdDate = new Date();

                alarmType.updatedBy = null;
                alarmType.updatedDate = null;
            }

            saveAlarmTypes(settings.token, [alarmType],
                (response) => {
                    if (!response.data.error) {
                        clearSelection();
                        setSnackbar("Record added/updated successfully.");
                        setReload(!reload);
                    }
                    else {
                        setDialog(response.data.errors[0], true);
                    }

                    setLoading(false);
                },
                (error) => {
                    setDialog(error.message, true);
                    setLoading(false);
                }
            );
        }
    }

    const handleNotifcationChange = (event) => {
        setIsNotification(event.target.checked);
        selectedRow.notification = event.target.checked;
    };

    const handleDelayedChange = (event) => {
        setIsDelayed(event.target.checked);
        selectedRow.delayed = event.target.checked;
    };

    const handleOnChange = (event) => {
        if (event.target.name === 'priorityId' && event.target.value > 0) {
            setSelectedPriority(priorityList.find((item) => item.id === event.target.value));
        }
        else if (event.target.name === 'sourceId') {
            setSelectedSource(sourceList.find((item) => item.sourceId === event.target.value));
        }

        selectedRow[event.target.name] = event.target.value;
    };

    const handleCancelDelete = () => {
        setOpenDelete(false);
        setReload(!reload);
    };

    const doDelete = () => {
        setOpenDelete(false);

        if (Object.keys(selectedRow).length > 0) {
            setLoading(true);

            deleteAlarmTypes(settings.token, [selectedRow.id],
                (response) => {
                    if (!response.data.error) {
                        clearSelection();
                        setSnackbar("Alarm Type deleted successfully.");
                        setReload(!reload);
                    }
                    else {
                        setDialog(response.data.errors[0], true);
                    }

                    setLoading(false);
                },
                (error) => {
                    setDialog(error.message, true);
                    setLoading(false);
                }
            );
        }
    };

    const handleAddClick = (event) => {
        setAnchor(event.currentTarget);
    }

    const handleMenuClose = (event) => {
        setAnchor(null);
    };

    const handleImportFile = (event) => {
        setAnchor(null);

        importFile.current.click();
    };

    const handleFileChange = (event) => {
        const formData = new FormData();
        formData.append("file", event.target.files[0]);
        setLoading(true);

        importAlarmFile(settings.token, formData,
            (response) => {
                if (!response.data.error) {
                    setDialog(JSON.parse(response.data.result).join('\n'));
                    clearSelection();
                    setReload(!reload);
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        );
    };

    const handleGroupChange = (event) => {
        if (event.target.checked) {
            setSelectedGroups([...selectedGroups, event.target.value]);
        }
        else {
            setSelectedGroups(selectedGroups.filter((id) => id !== event.target.value));
        }
    };

    const handleFilter = () => {
        getData(filter.current, page, pageSize);
    };

    const handleClearFilter = () => {
        getData(filter.current, page, pageSize);
    };

    const handleDestinationChange = (id) => (event) => {

        setSelectedRow({ ...selectedRow, [event.target.id]: event.target.checked ? event.target.value : null });

        setGridRows((prevRows) => {
            return prevRows.map((row) => row.id === id ?
                {
                    ...row,
                    [event.target.id]: event.target.checked ? event.target.value : null
                }
                : row);
        });
    }

    React.useEffect(() => {
        setLoading(true);

        getAlarmGroups(settings.token,
            (response) => {
                if (!response.data.error) {
                    setAlarmGroups(JSON.parse(response.data.result));
                }
                else {
                    setDialog(response.data.errors[0], true);
                }

                setLoading(false);
            },
            (error) => {
                setDialog(error.message, true);
                setLoading(false);
            }
        );

        setGridColumns(columns);
    }, []);

    React.useEffect(() => {
        if (Object.keys(settings).length > 0) {
            getData(filter.current, page, pageSize);
        }
    }, [settings, reload]);

    React.useEffect(() => {
        if (SHOW_GROUPS) {
            const typeCols = alarmGroups.map((item) => {
                return (
                    {
                        field: item.name,
                        headerName: item.name,
                        flex: 0.1,
                        renderCell: (props) => {
                            return (
                                props.row.alarmGroupIds && props.row.alarmGroupIds.includes(item.id) ? <CheckIcon sx={{ ml: 1 }} /> : <CloseOutlinedIcon sx={{ ml: 1 }} />
                            )
                        }
                    }
                );
            });

            setGridColumns(columns.concat(typeCols));
        }
    }, [alarmGroups]);

    return (
        <>
            <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ width: "50%" }}>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '400', color: 'text.bright' }} style={{ fontSize: "25px" }}>Alarm Types</Typography>
                    <Typography sx={{ marginLeft: "5px", fontWeight: '300', color: 'text.secondary' }} style={{ fontSize: "15px" }}>Define source system alarm types, priorities and destinations.</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: "50%", mb: 4 }}>
                    <Button variant="outlined" color="error"
                        style={{ width: "120px" }}
                        onClick={handleDelete}
                        sx={{ display: canDelete ? 'flex' : 'none' }}
                    >
                        Delete
                    </Button>
                    <Button variant="outlined" style={{ width: "120px", marginLeft: "7px" }} onClick={handleEdit}>Edit</Button>
                    <Button variant="contained" color="success"
                        style={{ width: "120px", marginLeft: "7px" }}
                        endIcon={<KeyboardArrowDownIcon />}
                        onClick={handleAddClick}
                        disableElevation
                        aria-haspopup="true"
                    >
                        Add
                    </Button>
                    <Menu
                        open={open}
                        onClose={handleMenuClose}
                        elevation={0}
                        anchorEl={anchor}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        sx={{
                            marginTop: 1
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    minWidth: 150,
                                    border: 1,
                                    borderColor: 'divider'
                                },
                            }
                        }}
                    >
                        <MenuItem onClick={handleNew} disableRipple sx={{ justifyContent: 'flex-end' }}>
                            Add new Alarm Type
                        </MenuItem>
                        <MenuItem onClick={handleImportFile} disableRipple sx={{ justifyContent: 'flex-end' }}>
                            Import from file
                        </MenuItem>
                    </Menu>
                </Box>
                <input
                    type='file'
                    id='import-file'
                    ref={importFile}
                    style={{ display: 'none' }}
                    accept='.csv,.txt'
                    onChange={handleFileChange}
                />
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Container maxWidth="xl">

                <Grid container gap={2} sx={{ mt: 2 }}>
                    <Grid item gap={2} sx={{ display: 'flex', width: '100%' }}>
                        <CustomFilter filter={filter}
                            showDates={false}
                            onFilter={handleFilter}
                            onClearFilter={handleClearFilter}
                        />
                    </Grid>
                    <Box sx={{ width: '100%' }}>
                        <LinearProgress sx={{ display: loading ? 'flex' : 'none' }} />
                        <CustomDataGrid
                            columns={gridColumns}
                            paginationMode='server'

                            rows={gridRows}
                            rowCount={serverData.totalRecords}
                            getRowId={(row) => row.id}
                            onCellDoubleClick={handleCellDoubleClick}

                            pagination
                            pageSizeOptions={[10, 20, 30, 50]}
                            onPaginationModelChange={handlePageModelChanged}
                            onRowSelectionModelChange={handleRowSelected}

                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: defaultPageSize,
                                    },
                                },
                            }}
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }

                            rowSelectionModel={gridSelection}
                            autoHeight
                        />
                    </Box>
                    <Dialog
                        open={openDialog}
                        onClose={handleCloseDialog}
                        maxWidth="sm"
                        fullWidth={true}
                        PaperProps={{
                            component: "form",
                            onSubmit: (handleSubmitDialog)
                        }}
                    >
                        <DialogTitle>Alarm Type</DialogTitle>
                        <DialogContent>
                            <Box sx={{ display: "flex", gap: 2 }} spacing={2}>
                                <FormControl variant="standard" sx={{ mt: 1, flexGrow: 0.5 }}>
                                    <InputLabel id="source-label" required>Message Source</InputLabel>
                                    <Select
                                        labelId="source-label"
                                        id="source-select"
                                        name="sourceId"
                                        required
                                        sx={{ width: '100%' }}
                                        value={openDialog ? selectedRow.sourceId : ''}
                                        onChange={handleOnChange}
                                    >
                                        {sourceList.map((item, index) => (
                                            <MenuItem key={index} value={item.sourceId}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl variant="standard" sx={{ mt: 1, flexGrow: 0.5 }}>
                                    <InputLabel id="source-label" required>Alarm Priority</InputLabel>
                                    <Select
                                        labelId="priority-label"
                                        id="priority-select"
                                        name="priorityId"
                                        required
                                        sx={{ width: '100%' }}
                                        value={openDialog ? selectedRow.priorityId : ''}
                                        onChange={handleOnChange}
                                    >
                                        {priorityList.map((item, index) => (
                                            <MenuItem key={index} value={item.id}>{item.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                            </Box>
                            <Box sx={{ display: "flex", mt: 1, gap: 2 }} spacing={2}>
                                <TextField sx={{ flexGrow: 1 }}
                                    margin="dense"
                                    id="alarmType"
                                    name="alarmType"
                                    label="Alarm Type"
                                    type="text"
                                    required
                                    defaultValue={openDialog ? selectedRow.alarmType : ''}
                                    onChange={handleOnChange}
                                    variant="standard"
                                />
                            </Box>

                            <Box sx={{ display: SHOW_DESTINATIONS ? 'flex' : 'none', mt: 2 }} >
                                <Typography sx={{ fontWeight: 500 }} color="heading">Alarm Destinations:</Typography>
                            </Box>
                            {/*
                              * Two fixed columns rather than nested flex rows. The rows used to size
                              * their first column with flexGrow, which makes its width depend on how
                              * much free space the row has - and that varies with the label in the
                              * second column, so 'Push Notification' being wider than 'Alarm Viewer'
                              * pulled the second column out of line between the rows. A grid gives both
                              * rows the same column edges whatever the labels say.
                              */}
                            <Box sx={{ display: SHOW_DESTINATIONS ? 'grid' : 'none', gridTemplateColumns: DIALOG_COLUMNS, columnGap: 2 }}>
                                <FormControlLabel
                                    control={<Checkbox id='route01' checked={selectedRow.route01 !== null} />}
                                    label='Planon (CAFM)'
                                    value='esb.cafm-out'
                                    onChange={handleDestinationChange(selectedRow.id)}
                                />
                                <FormControlLabel
                                    control={<Checkbox id='route02' checked={selectedRow.route02 !== null} />}
                                    label='Alarm Viewer'
                                    value='esb.twx-alarms'
                                    onChange={handleDestinationChange(selectedRow.id)}
                                />
                                <FormControlLabel
                                    control={<Checkbox id='route03' checked={selectedRow.route03 !== null} />}
                                    label='Group Email'
                                    value='esb.email-out'
                                    onChange={handleDestinationChange(selectedRow.id)}
                                />
                                {/*
                                  * The global switch for push. Ticking it is necessary but not
                                  * sufficient: each alarm group then opts in separately, on the screen
                                  * reached by double-clicking a group - the same two-level gate as Group
                                  * Email. Always offered, like Planon, which is also shown everywhere and
                                  * does nothing unless something consumes its topic.
                                  *
                                  * Second column, so it sits directly under Alarm Viewer.
                                  */}
                                <FormControlLabel
                                    control={<Checkbox id='route04' checked={selectedRow.route04 !== null} />}
                                    label='Push Notification'
                                    value='esb.push-out'
                                    onChange={handleDestinationChange(selectedRow.id)}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', mt: 2 }} >
                                <Typography sx={{ fontWeight: 500 }} color="heading">Options:</Typography>
                            </Box>
                            {/* Same columns as Alarm Destinations above - each cell holds its
                              * checkbox and that checkbox's helper text together. */}
                            <Box sx={{ display: 'grid', gridTemplateColumns: DIALOG_COLUMNS, columnGap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }} >
                                    <FormControlLabel
                                        control={<Checkbox checked={isNotification} />}
                                        label="Set as Notification"
                                        onChange={handleNotifcationChange}
                                        aria-describedby="notification-helper-text"
                                    />
                                    <FormHelperText id="notification-helper-text">
                                        * Cleared from Alarm Viewer regularly.
                                    </FormHelperText>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }} >
                                    <FormControlLabel
                                        control={<Checkbox checked={isDelayed} />}
                                        label="User Acknowledged as Inactive"
                                        onChange={handleDelayedChange}
                                        aria-describedby="delayed-helper-text"
                                    />
                                    <FormHelperText id="delayed-helper-text">
                                        * Requires an acknowledgment to clear the alarm.
                                    </FormHelperText>
                                </Box>
                            </Box>
                            <Box sx={{ display: SHOW_GROUPS ? 'flex' : 'none', flexDirection: 'column', mt: 2 }}>
                                <Typography sx={{ fontWeight: 500 }} color="heading">Enable notifications for:</Typography>
                                <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
                                    {alarmGroups.map((item, index) => (
                                        <FormControlLabel key={index}
                                            control={<Checkbox checked={selectedGroups.includes(item.id.toString())} />}
                                            value={item.id}
                                            label={item.name}
                                            sx={{ width: 160 }}
                                            onChange={handleGroupChange}
                                        />
                                    ))}
                                </FormGroup>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Cancel</Button>
                            <Button type="submit" variant="contained">Save</Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            </Container>
            <ConfirmDialog open={openDelete} title={"Confirm Delete"}
                content={"Are you sure you want to remove this alarm type? (Note: this cannot be undone)"}
                onNoClick={handleCancelDelete}
                onYesClick={doDelete}
                yesNo
            />
        </>
    )
}

export default AlarmTypes;
